import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { WITHDRAWS_ENABLED } from '../../constants/featureFlags';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFinalizeWithdraw } from '../../hooks/useFinalizeWithdraw';
import { useModalState } from '../../hooks/useModalState';
import { usePendingWithdraw } from '../../hooks/usePendingWithdraw';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { formatLockoutDuration } from '../../lib/formatters';
import { withdrawCountdownTooltip } from '../../lib/tooltips';
import { Button, ButtonProps } from '../catalyst-ui-kit/button';
import { Tooltip } from '../Tooltip/Tooltip';

export type WithdrawButtonUiProps = {
  lockoutTime?: number;
  pendingWithdrawPending: boolean;
  finalizeWithdrawPending: boolean;
  pendingWithdrawFetched: boolean;
  icon?: React.ReactNode;
} & ButtonProps;

export const WithdrawButtonUi: React.FC<WithdrawButtonUiProps> = ({
  lockoutTime,
  pendingWithdrawPending,
  disabled,
  finalizeWithdrawPending,
  pendingWithdrawFetched,
  icon,
  ...props
}) => {
  const { value: withdrawEnabled } = useFeatureFlag(WITHDRAWS_ENABLED);
  // time remaining in lockout
  const [lockout, setLockout] = useState(lockoutTime ? lockoutTime - Date.now() : 0);
  const selectedSubaccount = useActiveSelectedSubaccount();
  useEffect(() => {
    let intervalReference: NodeJS.Timeout;

    if (lockoutTime && lockoutTime > Date.now()) {
      // Only set the lockout on the leading edge
      if (!lockout) {
        const newTime = lockoutTime - Date.now();
        setLockout(newTime > 0 ? newTime : 0);
      }

      intervalReference = setTimeout(() => {
        const newTime = lockoutTime - Date.now();
        setLockout(newTime > 0 ? newTime : 0);
      }, 1000);
    } else {
      setLockout(0);
    }

    return () => {
      if (intervalReference) clearInterval(intervalReference);
    };
  }, [lockout, lockoutTime]);

  const isCountdown = lockout > 0;
  const isDisabled = pendingWithdrawPending || isCountdown || disabled || finalizeWithdrawPending;
  const buttonText = (() => {
    if (!selectedSubaccount || !pendingWithdrawFetched) return 'Withdraw';
    if (finalizeWithdrawPending) return 'Claiming...';
    if (lockoutTime !== undefined && lockout <= 0) return 'Claim Withdraw';
    if (pendingWithdrawPending) return 'Withdrawing...';
    if (isCountdown) return `Withdraw (${formatLockoutDuration(lockout)})`;
    return 'Withdraw';
  })();
  return (
    <Tooltip
      className="w-full"
      placement="top-end"
      content={isCountdown && lockoutTime ? withdrawCountdownTooltip(lockoutTime) : undefined}
    >
      <Button
        disabled={isDisabled || !withdrawEnabled}
        {...props}
        className={twMerge(props.className, 'text-nowrap')}
      >
        {icon} {buttonText}
      </Button>
    </Tooltip>
  );
};

export type WithdrawButtonProps = {
  icon?: React.ReactNode;
  onBeforeOpenModal?: () => void | Promise<void>;
} & ButtonProps;

export const WithdrawButton: React.FC<WithdrawButtonProps> = ({
  icon,
  onBeforeOpenModal,
  ...props
}) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const {
    data: pendingWithdraws,
    isPending,
    isFetched,
  } = usePendingWithdraw(selectedSubaccount?.id);
  const pendingWithdraw = pendingWithdraws?.[0];
  const { isPending: finalizeWithdrawPending, mutate: finalizeWithdraw } = useFinalizeWithdraw();
  const setWithdrawalModal = useModalState((state) => state.setWithdrawalModal);

  const handleClick = async () => {
    if (!pendingWithdraw) {
      // No pending withdrawal, open the modal
      await onBeforeOpenModal?.();
      setWithdrawalModal('open');
    } else if (pendingWithdraw.readyAt && Date.now() >= pendingWithdraw.readyAt) {
      // Pending withdrawal is ready, finalize it
      if (selectedSubaccount) {
        finalizeWithdraw(pendingWithdraw);
      }
    }
  };

  return (
    <WithdrawButtonUi
      {...props}
      onClick={handleClick}
      icon={icon}
      lockoutTime={pendingWithdraw?.readyAt}
      pendingWithdrawPending={isPending || Boolean(pendingWithdraw && !pendingWithdraw.readyAt)}
      pendingWithdrawFetched={isFetched}
      finalizeWithdrawPending={finalizeWithdrawPending}
    />
  );
};
